import { User, PencilSimple } from "@phosphor-icons/react";

/**
 * Compact owner avatar + name pill used in list rows.
 * Falls back to "—" if owner is missing.
 *
 * Optional props for inline reassignment:
 *   onReassignClick: () => void   — when set, shows a pencil affordance and
 *                                   the whole cell becomes a button.
 */
export const OwnerCell = ({ owner, testId, onReassignClick }) => {
    const initials = (owner?.name || owner?.email || "?")
        .split(" ")
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
    const label = owner?.name || owner?.email || "Unassigned";

    if (!onReassignClick) {
        if (!owner) {
            return (
                <span className="text-xs text-slate-400" data-testid={testId}>
                    —
                </span>
            );
        }
        return (
            <div className="inline-flex items-center gap-1.5 min-w-0" data-testid={testId} title={`${label} · ${owner.role || ""}`}>
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[9px] font-bold text-white">
                    {initials || <User size={10} />}
                </span>
                <span className="truncate text-xs text-slate-700">{label}</span>
            </div>
        );
    }

    // Reassignable variant — entire cell is a button surfacing a hover pencil.
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onReassignClick(); }}
            data-testid={testId}
            title={`${label}${owner?.role ? ` · ${owner.role}` : ""} — click to reassign`}
            className="group inline-flex items-center gap-1.5 min-w-0 px-1.5 py-0.5 -mx-1.5 -my-0.5 rounded hover:bg-slate-100 transition-colors text-left"
        >
            <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[9px] font-bold text-white">
                {owner ? (initials || <User size={10} />) : <User size={10} />}
            </span>
            <span className={`truncate text-xs ${owner ? "text-slate-700" : "text-slate-400 italic"}`}>{label}</span>
            <PencilSimple size={10} className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
        </button>
    );
};

export default OwnerCell;
